import { AxiosResponse } from "axios";
import {
  KulionerEntites,
  Kulioner,
  KulionerOrder,
  KulionerMerchant,
  KulionerProduct,
  KulionerHistory,
  KulionerCycle,
  KulionerMerchantEntites,
  Merchant,
  UpdateMerchantOrderResponse,
  UpdateMerchantOrderRequest
} from "@/domain/entities/Kulioner";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";

export class KulionerMapper {
  private _mapKulionerOrderEntities(orders: any) {
    const merchants = orders.merchants ?? [];
    return new KulionerOrder({
      id: orders.id,
      status: orders.status,
      orderId: orders.order_id,
      orderShipmentId: orders.order_shipment_id,
      orderSttNo: orders.order_stt_no,
      isMarkProcessed: orders.is_mark_processed || false,
      createdAt: orders.created_at,
      updatedAt: orders.updated_at,
      productType: orders.product_type,
      totalPrice: orders.total_price,
      totalMerchant: orders.total_merchant,
      recipientPhoneNumber: orders.recipient_phone_number,
      recipientName: orders.recipient_name,
      recipientAddress: orders.recipient_address,
      cycleAt: orders.cycle_at,
      merchants: merchants.map((merchant: any) => {
        return new KulionerMerchant({
          name: merchant.name,
          phoneNumber: merchant.phone_number,
          address: merchant.address,
          thumbnail: merchant.thumbnail,
          latLon: merchant.lat_lon,
          totalProduct: merchant.total_product,
          totalQuantity: merchant.total_quantity,
          totalPrice: merchant.total_price,
          actualProduct: merchant.actual_product,
          actualQuantity: merchant.actual_quantity,
          isPurchased: merchant.is_purchased
        });
      })
    });
  }

  private _mapKulionerDetailOrderEntities(order: any) {
    const merchants = order.merchants ?? [];
    return new KulionerOrder({
      id: order.id,
      status: order.status,
      orderId: order.order_id,
      orderShipmentId: order.order_shipment_id,
      orderSttNo: order.order_stt_no,
      isMarkProcessed: order.is_mark_processed || false,
      createdAt: order.created_at,
      updatedAt: order.updated_at,
      productType: order.product_type,
      totalPrice: order.total_price,
      totalMerchant: order.total_merchant,
      recipientPhoneNumber: order.recipient_phone_number,
      recipientName: order.recipient_name,
      recipientAddress: order.recipient_address,
      cycleAt: order.cycle_at,
      merchants: merchants.map((merchant: any) => {
        return new KulionerMerchant({
          name: merchant.name,
          phoneNumber: merchant.phone_number,
          address: merchant.address,
          thumbnail: merchant.thumbnail,
          latLon: merchant.lat_lon,
          totalProduct: merchant.total_product,
          totalQuantity: merchant.total_quantity,
          totalPrice: merchant.total_price,
          isPurchased: merchant.is_purchased || false,
          actualProduct: merchant.actual_product || 0,
          actualQuantity: merchant.actual_quantity || 0,
          products: merchant.products.map((product: any) => {
            return new KulionerProduct({
              id: product.id,
              name: product.name,
              price: product.price,
              quantity: product.quantity,
              totalPrice: product.total_price,
              thumbnail: product.thumbnail,
              note: product.note,
              handlingType: product.handling_type,
              actualQuantity: product.actual_quantity,
              isPurchased: product.is_purchased,
              quantityUpdatedAt: product.quantity_updated_at
            });
          })
        });
      }),
      histories: order.histories?.length
        ? order.histories.map(
            (item: any) => new KulionerHistory(convertSnakeToCamel(item))
          )
        : []
    });
  }

  convertKulionerListFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    const ordersArr = data.orders ?? [];
    return new KulionerEntites({
      pagination: new PaginationV2({
        allTotalData: data.meta.total_records,
        page: data.meta.page,
        limit: data.meta.limit,
        totalData: data.meta.total_records
      }),
      data: new Kulioner({
        summary: data.summary,
        orders: ordersArr.map((orders: any) => {
          return this._mapKulionerOrderEntities(orders);
        })
      })
    });
  }

  convertKulionerDetailFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    return this._mapKulionerDetailOrderEntities(data);
  }

  convertKulionerListSummaryFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    const item = data ?? [];
    const countStatus = {} as any;
    item.forEach((item: any) => {
      countStatus[item.status] = item.total;
    });
    return countStatus;
  }

  convertKulionerCycleListFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    const cycles = data ?? [];
    return cycles.map(
      (cycle: Record<string, string | number[]>) => new KulionerCycle({
          cutOffTime: cycle.cut_off_time as string,
          id: cycle.id as number[]
        })
    );
  }

  convertKulionerMerchantListFromApi(res: AxiosResponse<any>) {
    const {
      data: { data }
    } = res;
    const ordersArr = data.orders ?? [];
    return new KulionerMerchantEntites({
      data: new Merchant({
        canBeProcessed: data.can_be_processed || false,
        eligibleOrderIds: data.eligible_order_ids || [],
        orderIds: data.order_ids || [],
        orders: ordersArr.map((orders: any) => {
          return new KulionerMerchant({
            id: orders.id,
            name: orders.name,
            phoneNumber: orders.phone_number,
            address: orders.address,
            thumbnail: orders.thumbnail,
            latLon: orders.lat_lon,
            isPurchased: orders.is_purchased || false,
            products: orders.products.map((product: any) => {
              return new KulionerProduct({
                id: product.id,
                name: product.name,
                thumbnail: product.thumbnail,
                quantity: product.quantity,
                price: product.price,
                actualQuantity: product.actual_quantity,
                isQuantityUpdated: product.is_quantity_updated || false,
              });
            })
          })
        })
      })
    });
  }

  convertKulionerUpdateMerchantOrderFromApi(
    res: AxiosResponse<any>
  ): UpdateMerchantOrderResponse {
    const {
      data: { updatedMerchant }
    } = res;

    return new UpdateMerchantOrderResponse({
      ...convertSnakeToCamel(updatedMerchant),
      products: updatedMerchant?.products?.length
        ? updatedMerchant.products.map(
            (item: any) =>
              new UpdateMerchantOrderRequest(convertSnakeToCamel(item))
          )
        : []
    });
  }
}
